import { Service } from '@toeverything/infra';

import { prepareDatabaseSystemDocsForServerMutation } from '../../doc/services/database-system-doc-access';
import type { AccessibleDocCacheStore } from '../../doc/stores/accessible-doc-cache';
import {
  OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import type {
  OfflineMutationOperation,
  OfflineMutationRecord,
} from '../../offline-mutation-outbox/types';
import type { WorkspaceService } from '../../workspace';
import { ViewBackedAppApiClientService } from './api-client';
import { ViewBackedAppServerUnavailableError } from './http';
import { ViewBackedAppRegistryService } from './registry';
import { viewBackedAppResourceId } from './resource-id';
import type {
  ViewBackedAppDescriptor,
  ViewBackedAppSourceAttachInput,
  ViewBackedAppSourceMutationName,
  ViewBackedAppSourcePreferencePatch,
} from './types';
import { decodeViewBackedAppSourcesResponseV1 } from './wire-contract';

export type ViewBackedAppSourceMutationOperation = Extract<
  OfflineMutationOperation,
  `${string}.${ViewBackedAppSourceMutationName}`
>;

type NormalizedViewBackedAppSourceAttachPayload =
  ViewBackedAppSourceAttachInput & {
    blockId: string;
    viewId: string;
  };

type ViewBackedAppSourceServerTarget = Pick<
  ViewBackedAppSourceAttachInput,
  'databaseId' | 'docId'
>;

export type ViewBackedAppMutationRecord =
  OfflineMutationRecord<ViewBackedAppSourceMutationOperation>;

export class ViewBackedAppMutationOutboxService extends Service {
  readonly pendingMutations$ = this.outbox.pendingMutations$;
  readonly failedMutations$ = this.outbox.failedMutations$;
  private readonly registeredSourceExecutorAppKinds = new Set<string>();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly api: ViewBackedAppApiClientService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly accessibleDocCache: AccessibleDocCacheStore,
    registry: ViewBackedAppRegistryService
  ) {
    super();
    this.disposables.push(
      registry.onDescriptorRegistered(descriptor =>
        this.registerSourceExecutors(descriptor)
      )
    );
  }

  enqueueCreateSource(
    descriptor: ViewBackedAppDescriptor,
    input: ViewBackedAppSourceAttachInput
  ): Promise<unknown> {
    return this.outbox.enqueue({
      operation: sourceOperation(descriptor, 'createSource'),
      payload: normalizeAttachPayload(descriptor, input),
      idempotencyKey: attachIdempotencyKey(
        descriptor,
        this.workspaceService.workspace.id,
        'createSource',
        input
      ),
    });
  }

  enqueueAttachSource(
    descriptor: ViewBackedAppDescriptor,
    input: ViewBackedAppSourceAttachInput
  ): Promise<unknown> {
    return this.outbox.enqueue({
      operation: sourceOperation(descriptor, 'attachSource'),
      payload: normalizeAttachPayload(descriptor, input),
      idempotencyKey: attachIdempotencyKey(
        descriptor,
        this.workspaceService.workspace.id,
        'attachSource',
        input
      ),
    });
  }

  enqueueUpdateSourcePreference(
    descriptor: ViewBackedAppDescriptor,
    sourceId: string,
    patch: ViewBackedAppSourcePreferencePatch
  ): Promise<unknown> {
    return this.outbox.enqueue({
      operation: sourceOperation(descriptor, 'updateSourcePreference'),
      payload: { sourceId, patch },
      idempotencyKey: [
        sourceOperation(descriptor, 'updateSourcePreference'),
        this.workspaceService.workspace.id,
        sourceId,
        patch.visible ?? '-',
        patch.colorOverride ?? '-',
        patch.titleOverride ?? '-',
      ].join(':'),
    });
  }

  async prepareSourceForServerMutation(
    descriptor: ViewBackedAppDescriptor,
    target: ViewBackedAppSourceServerTarget
  ): Promise<void> {
    const { docId } = target;
    // A server-known page may still contain freshly created local database
    // root/row-shard docs. Always flush the complete database system-doc
    // shape before a server mutation; only the visible page confirmation is
    // conditional on the page itself still being locally created.
    await this.flushSourceDocuments(target);
    if (!this.accessibleDocCache.isLocallyCreated(docId)) return;
    if (!(await this.waitForDocServerConfirmation(docId))) {
      throw new OfflineMutationTransientError(
        `${descriptor.appKind} source doc server confirmation pending`
      );
    }
  }

  private registerSourceExecutors(descriptor: ViewBackedAppDescriptor): void {
    if (this.registeredSourceExecutorAppKinds.has(descriptor.appKind)) return;
    this.registeredSourceExecutorAppKinds.add(descriptor.appKind);
    const disposables: Array<() => void> = [];
    if (supportsSourceOperation(descriptor, 'createSource')) {
      disposables.push(
        this.outbox.registerExecutor(
          sourceOperation(descriptor, 'createSource'),
          async mutation => {
            const payload = mutation.payload as ViewBackedAppSourceAttachInput;
            await this.replayAttachSource(
              descriptor,
              mutation.workspaceId,
              payload
            );
          }
        )
      );
    }
    if (supportsSourceOperation(descriptor, 'attachSource')) {
      disposables.push(
        this.outbox.registerExecutor(
          sourceOperation(descriptor, 'attachSource'),
          async mutation => {
            const payload = mutation.payload as ViewBackedAppSourceAttachInput;
            await this.replayAttachSource(
              descriptor,
              mutation.workspaceId,
              payload
            );
          }
        )
      );
    }
    if (supportsSourceOperation(descriptor, 'updateSourcePreference')) {
      disposables.push(
        this.outbox.registerExecutor(
          sourceOperation(descriptor, 'updateSourcePreference'),
          async mutation => {
            const payload = mutation.payload as {
              patch: ViewBackedAppSourcePreferencePatch;
              sourceId: string;
            };
            await this.runRequest(() =>
              this.api.updateSourcePreference(
                descriptor,
                mutation.workspaceId,
                payload.sourceId,
                payload.patch
              )
            );
          }
        )
      );
    }
    this.disposables.push(...disposables);
  }

  private async replayAttachSource(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string,
    payload: ViewBackedAppSourceAttachInput
  ): Promise<void> {
    await this.ensureDocServerReady(descriptor, payload);
    try {
      await this.runRequest(() =>
        this.api.attachSource(descriptor, workspaceId, payload)
      );
    } catch (error) {
      // The host page can already be server-confirmed while a freshly created
      // database root/row-shard is still waiting for DocPersist
      // materialization. Gate this retry on the precise server error, not on
      // the host page's local-created flag.
      if (isAttachMaterializationPending(error)) {
        await this.flushSourceDocuments(payload);
        try {
          await this.runRequest(() =>
            this.api.attachSource(descriptor, workspaceId, payload)
          );
          return;
        } catch (retryError) {
          if (isAttachMaterializationPending(retryError)) {
            throw new OfflineMutationTransientError(
              `${descriptor.appKind} source server materialization pending`
            );
          }
          throw retryError;
        }
      }
      if (
        !(await this.isSourceAlreadyVisible(
          descriptor,
          workspaceId,
          payload,
          error
        ))
      ) {
        throw error;
      }
    }
  }

  private async ensureDocServerReady(
    descriptor: ViewBackedAppDescriptor,
    payload: NormalizedViewBackedAppSourceAttachPayload
  ): Promise<void> {
    await this.prepareSourceForServerMutation(descriptor, payload);
  }

  private async flushSourceDocuments(
    payload: ViewBackedAppSourceServerTarget
  ): Promise<void> {
    const workspace = this.workspaceService.workspace;
    const docEngine = workspace.engine?.doc;
    if (!docEngine?.flushAndWaitForSynced) return;

    try {
      await prepareDatabaseSystemDocsForServerMutation({
        collection: workspace.docCollection,
        docEngine,
        docId: payload.docId,
        workspaceId: workspace.id,
      });
    } catch (error) {
      const detail =
        error instanceof Error ? error.message : 'unknown sync failure';
      throw new OfflineMutationTransientError(
        `View-backed app source documents are still syncing: ${detail}`
      );
    }
  }

  private waitForDocServerConfirmation(docId: string): Promise<boolean> {
    return this.accessibleDocCache.waitForServerConfirmed(docId, {
      timeoutMs: 10_000,
      pollMs: 250,
    });
  }

  private async isSourceAlreadyVisible(
    descriptor: ViewBackedAppDescriptor,
    workspaceId: string,
    payload: ViewBackedAppSourceAttachInput,
    error: unknown
  ): Promise<boolean> {
    if (!isDuplicateOrAlreadyExistsError(error)) return false;
    const sourceId = viewBackedAppResourceId(
      descriptor,
      payload.docId,
      payload.databaseId,
      payload.viewId
    );
    const response = decodeViewBackedAppSourcesResponseV1(
      await this.runRequest(() => this.api.listSources(descriptor, workspaceId))
    );
    return response.sources.some(
      source =>
        source.id === sourceId &&
        source.docId === payload.docId &&
        source.databaseId === payload.databaseId &&
        source.viewId === payload.viewId
    );
  }

  private async runRequest<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (error) {
      if (error instanceof ViewBackedAppServerUnavailableError) {
        throw new OfflineMutationTransientError(
          'Workspace server is not available'
        );
      }
      throw error;
    }
  }
}

export function isViewBackedAppSourceMutation(
  mutation: OfflineMutationRecord
): mutation is ViewBackedAppMutationRecord {
  return (
    mutation.operation.endsWith('.createSource') ||
    mutation.operation.endsWith('.attachSource') ||
    mutation.operation.endsWith('.updateSourcePreference')
  );
}

export function sourceOperation(
  descriptor: Pick<
    ViewBackedAppDescriptor,
    'appKind' | 'sourceMutationOperations'
  >,
  operation: ViewBackedAppSourceMutationName
): ViewBackedAppSourceMutationOperation {
  if (!supportsSourceOperation(descriptor, operation)) {
    throw new Error(
      `Unsupported ${descriptor.appKind} source mutation "${operation}"`
    );
  }
  const value =
    `${descriptor.appKind}.${operation}` as ViewBackedAppSourceMutationOperation;
  return value;
}

export function supportsSourceOperation(
  descriptor: Pick<ViewBackedAppDescriptor, 'sourceMutationOperations'>,
  operation: ViewBackedAppSourceMutationName
): boolean {
  return (descriptor.sourceMutationOperations ?? []).includes(operation);
}

function normalizeAttachPayload(
  descriptor: ViewBackedAppDescriptor,
  input: ViewBackedAppSourceAttachInput
): NormalizedViewBackedAppSourceAttachPayload {
  return {
    ...input,
    blockId: requiredAttachPayloadField(descriptor, input.blockId, 'blockId'),
    viewId: requiredAttachPayloadField(descriptor, input.viewId, 'viewId'),
  };
}

function attachIdempotencyKey(
  descriptor: ViewBackedAppDescriptor,
  workspaceId: string,
  operation: 'attachSource' | 'createSource',
  input: ViewBackedAppSourceAttachInput
): string {
  return [
    sourceOperation(descriptor, operation),
    workspaceId,
    input.docId,
    input.databaseId,
    requiredAttachPayloadField(descriptor, input.viewId, 'viewId'),
  ].join(':');
}

function requiredAttachPayloadField(
  descriptor: Pick<ViewBackedAppDescriptor, 'appKind'>,
  value: string | null | undefined,
  label: string
): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    throw new Error(`${descriptor.appKind} source ${label} required`);
  }
  return trimmed;
}

function isDuplicateOrAlreadyExistsError(error: unknown): boolean {
  const message = errorDiagnosticText(error).toLowerCase();
  return (
    errorStatus(error) === 409 ||
    /already exists|duplicate|unique|already attached/.test(message)
  );
}

function isAttachMaterializationPending(error: unknown): boolean {
  const status = errorStatus(error);
  if (status !== 400 && status !== 404) return false;
  // WorkspaceServerService.fetch turns non-2xx responses into
  // UserFriendlyError before fetchViewBackedAppJson can wrap the response in
  // ViewBackedAppHttpError. Classify the real fetch error by its stable HTTP
  // status/message contract instead of requiring one concrete error class.
  return /view not found|database block .*not found|database .*not found|document .*[$]database[$].*[$]root.*not found|root document .*not found|root document is unavailable|no indexed access binding|placement|projection .*missing/i.test(
    errorDiagnosticText(error)
  );
}

function errorStatus(error: unknown): number {
  if (!error || typeof error !== 'object') return 0;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : 0;
}

function errorDiagnosticText(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (!error || typeof error !== 'object') return '';
  return [
    (error as { message?: unknown }).message,
    (error as { code?: unknown }).code,
    (error as { type?: unknown }).type,
    (error as { name?: unknown }).name,
  ]
    .filter(value => typeof value === 'string')
    .join(' ');
}
